import { useState } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { getPlate, type PlateDef } from '../data/plates';
import { tripScore } from '../game/scoring';
import { PlateMini } from './PlateMini';
import type { TripState } from '../game/types';

/**
 * A picture of a finished trip, made entirely on the phone that played it.
 *
 * A trip ends in a rest stop with everyone comparing scores, and the only thing
 * that ever leaves the car is a screenshot. This takes that screenshot
 * deliberately: portrait, legible at chat-thumbnail size, and carrying the URL
 * so the car behind can join in.
 *
 * Nothing is fetched. The catalogue already ships in the bundle, the colours
 * come from the stylesheet that is already parsed, and the pictogram is the same
 * component the plate list draws - so a card renders in a Nullarbor blackspot
 * exactly as it does at home, which is where it will usually be asked for.
 */

const CARD_W = 1080;
/** Portrait 4:5 - the aspect ratio that survives being posted anywhere. */
const CARD_H = 1350;

const PAD = 80;
const INNER = CARD_W - PAD * 2;

const SCORE_H = 224;
const RAREST_H = 208;
const ROW_H = 84;
const ROW_GAP = 10;

/** Drawn at 1:1 into the card, so the SVG is asked for exactly this many pixels. */
const PICTOGRAM_W = 280;

const APP_NAME = 'Rego Plate Bingo';
const APP_URL = 'numberplate.pages.dev';

/* ------------------------------------------------------------------ palette */

interface Palette {
  bg: string;
  surface: string;
  ink: string;
  inkDim: string;
  inkFaint: string;
  line: string;
  accent: string;
  accentInk: string;
  font: string;
}

/*
 * The two values below are the only colours in this file. They are not a theme:
 * they are what a card falls back to when there is no parsed stylesheet to read
 * (jsdom, or a card asked for before the CSS lands), and exist only so it comes
 * out legible rather than transparent-on-transparent.
 */
const LAST_RESORT_INK = '#16181d';
const LAST_RESORT_PAPER = '#ffffff';
const LAST_RESORT_FONT = 'system-ui, sans-serif';

/**
 * Colours are read from the custom properties the app itself is painted with,
 * so the card matches the theme the player is actually looking at - and so a
 * palette change lands on the card without anyone remembering this file exists.
 */
function readPalette(): Palette {
  const style = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string): string =>
    style.getPropertyValue(name).trim() || fallback;

  return {
    bg: read('--bg', LAST_RESORT_PAPER),
    surface: read('--surface', LAST_RESORT_PAPER),
    ink: read('--ink', LAST_RESORT_INK),
    inkDim: read('--ink-dim', LAST_RESORT_INK),
    inkFaint: read('--ink-faint', LAST_RESORT_INK),
    line: read('--line', LAST_RESORT_INK),
    accent: read('--accent', LAST_RESORT_PAPER),
    accentInk: read('--accent-ink', LAST_RESORT_INK),
    font: read('--font', LAST_RESORT_FONT),
  };
}

/* ------------------------------------------------------------------ content */

export interface CardStanding {
  rank: number;
  name: string;
  score: string;
  isYou: boolean;
}

/**
 * Everything the card says, as strings, with no canvas anywhere near it.
 *
 * Splitting this out is what makes the card testable: jsdom has no 2D renderer,
 * so the drawing is exercised through a stand-in surface while the wording -
 * the part that can actually be wrong - is checked directly.
 */
export interface CardContent {
  title: string;
  date: string;
  score: string;
  plates: string;
  rarest: { plate: PlateDef; name: string; points: string } | null;
  standings: CardStanding[];
  appName: string;
  url: string;
}

const platesLabel = (count: number): string =>
  `${count.toLocaleString()} ${count === 1 ? 'plate' : 'plates'}`;

/**
 * The rarest thing spotted, which is the half of the card worth bragging about.
 *
 * Ids missing from this build's catalogue are skipped rather than counted as
 * zero, matching `scoreOf`: a save written by a newer client must not put a
 * plate on the card that this client cannot draw.
 */
export function rarestClaim(trip: TripState): PlateDef | null {
  let best: PlateDef | null = null;
  for (const id of Object.keys(trip.myClaims)) {
    const plate = getPlate(id);
    if (plate && (!best || plate.points > best.points)) best = plate;
  }
  return best;
}

/** `meId` is the viewer's public id, which marks their row on the standings. */
export function buildCardContent(trip: TripState, meId: string | null): CardContent {
  const rarest = rarestClaim(trip);

  return {
    title: trip.name,
    date: new Date(trip.startedAt).toLocaleDateString('en-AU'),
    score: tripScore(trip).toLocaleString(),
    plates: platesLabel(Object.keys(trip.myClaims).length),
    rarest: rarest
      ? { plate: rarest, name: rarest.name, points: `${rarest.points.toLocaleString()} points` }
      : null,
    // Three fits the card and is as far down as anyone reads anyway.
    standings: trip.leaderboard.slice(0, 3).map((entry, index) => ({
      rank: index + 1,
      name: entry.displayName,
      score: entry.score.toLocaleString(),
      isYou: entry.publicId === meId,
    })),
    appName: APP_NAME,
    url: APP_URL,
  };
}

/** The caption that rides along with the image where the share target takes one. */
export function shareText(content: CardContent): string {
  return `${content.title}: ${content.score} points, ${content.plates} on ${content.appName}. ${content.url}`;
}

/** A filename that still means something in a downloads folder six months later. */
export function cardFilename(trip: TripState): string {
  const slug = trip.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `rego-bingo-${slug || 'trip'}.png`;
}

/* --------------------------------------------------------------- pictogram */

interface PlateImage {
  src: string;
  width: number;
  height: number;
}

/**
 * The plate pictogram, as a data URL.
 *
 * Redrawing the plate in canvas 2D would be a second implementation of
 * PlateMini that drifted the first time either one changed, so the real
 * component is rendered into a detached node and serialised instead.
 *
 * `renderToStaticMarkup` is the obvious tool and is what scripts/plate-sheet.tsx
 * uses, but react-dom/server is a build-time dependency; pulling a whole second
 * renderer into the client bundle for one SVG is not a trade worth making.
 * `flushSync` gets the same markup out of the renderer that is already here.
 *
 * PlateMini references no external assets, so the image this produces does not
 * taint the canvas and `toBlob` still returns pixels.
 */
export function plateImage(plate: PlateDef, width: number): PlateImage {
  const host = document.createElement('div');
  const root = createRoot(host);

  try {
    flushSync(() => root.render(<PlateMini plate={plate} width={width} />));

    const svg = host.querySelector('svg');
    if (!svg) throw new Error('The plate pictogram rendered nothing');

    // XMLSerializer emits the SVG namespace declaration itself, which is what an
    // <img> needs before it will parse the data URL at all.
    const markup = new XMLSerializer().serializeToString(svg);

    return {
      src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`,
      width,
      // Taken off the element rather than recomputed, so PlateMini keeps sole
      // ownership of its own box proportions.
      height: Number(svg.getAttribute('height')) || width,
    };
  } finally {
    root.unmount();
  }
}

/* ----------------------------------------------------------------- drawing */

/**
 * `ctx.roundRect` only reached Safari in 16, and this game is played on whatever
 * phone is in the glovebox, so the corners are drawn by hand.
 */
function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, radius: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function wrapText(ctx: CanvasRenderingContext2D, value: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = '';

  for (const word of value.split(/\s+/).filter(Boolean)) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);

  return lines.length > 0 ? lines : [''];
}

function ellipsise(ctx: CanvasRenderingContext2D, value: string, maxWidth: number): string {
  if (ctx.measureText(value).width <= maxWidth) return value;

  let cut = value;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > maxWidth) {
    cut = cut.slice(0, -1);
  }
  return `${cut.trimEnd()}…`;
}

/**
 * Type that shrinks before it truncates.
 *
 * A trip called "Broken Hill to Coober Pedy" and a plate called "ACT Feel the
 * Power of Canberra" are the whole reason someone is sharing the card, so they
 * get to be smaller rather than cut off. Clipping is the last resort, not the
 * first.
 */
function fitType(
  ctx: CanvasRenderingContext2D,
  value: string,
  maxWidth: number,
  weight: number,
  sizes: readonly [number, ...number[]],
  family: string,
  maxLines: number,
): { size: number; lines: string[] } {
  let size = sizes[0];
  let lines: string[] = [value];

  for (const candidate of sizes) {
    size = candidate;
    ctx.font = `${weight} ${candidate}px ${family}`;
    lines = wrapText(ctx, value, maxWidth);
    if (lines.length <= maxLines) return { size, lines };
  }

  return {
    size,
    lines: [
      ...lines.slice(0, maxLines - 1),
      ellipsise(ctx, lines.slice(maxLines - 1).join(' '), maxWidth),
    ],
  };
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  content: CardContent,
  palette: Palette,
  pictogram: { image: CanvasImageSource; width: number; height: number } | null,
): void {
  const type = (weight: number, size: number): void => {
    ctx.font = `${weight} ${size}px ${palette.font}`;
  };

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = palette.bg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  let y = 84;

  type(800, 28);
  ctx.fillStyle = palette.inkFaint;
  ctx.fillText('ROAD TRIP', PAD, y + 28);
  y += 62;

  const title = fitType(ctx, content.title, INNER, 800, [76, 64, 54], palette.font, 2);
  ctx.fillStyle = palette.ink;
  for (const line of title.lines) {
    y += title.size;
    ctx.fillText(line, PAD, y);
    y += Math.round(title.size * 0.16);
  }
  y += 14;

  type(600, 32);
  ctx.fillStyle = palette.inkDim;
  ctx.fillText(content.date, PAD, y + 26);
  y += 62;

  // The headline number, on the app's accent so it reads first at thumbnail size.
  ctx.fillStyle = palette.accent;
  roundedRect(ctx, PAD, y, INNER, SCORE_H, 30);
  ctx.fill();

  ctx.fillStyle = palette.accentInk;
  type(800, 128);
  ctx.fillText(content.score, PAD + 40, y + 148);
  const scoreWidth = ctx.measureText(content.score).width;
  type(800, 30);
  ctx.fillText('POINTS', PAD + 58 + scoreWidth, y + 148);
  type(700, 36);
  ctx.fillText(content.plates, PAD + 40, y + 196);
  y += SCORE_H + 26;

  if (content.rarest) {
    ctx.fillStyle = palette.surface;
    roundedRect(ctx, PAD, y, INNER, RAREST_H, 30);
    ctx.fill();
    ctx.strokeStyle = palette.line;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Pictogram left, words right - the same reading order as a plate row.
    let textX = PAD + 40;
    if (pictogram) {
      ctx.drawImage(
        pictogram.image,
        PAD + 36,
        y + Math.round((RAREST_H - pictogram.height) / 2),
        pictogram.width,
        pictogram.height,
      );
      textX = PAD + 36 + pictogram.width + 32;
    }
    const textWidth = CARD_W - PAD - 36 - textX;

    type(800, 26);
    ctx.fillStyle = palette.inkFaint;
    ctx.fillText('RAREST FIND', textX, y + 62);

    const name = fitType(ctx, content.rarest.name, textWidth, 750, [46, 40, 34, 30], palette.font, 1);
    ctx.fillStyle = palette.ink;
    ctx.fillText(name.lines[0] ?? '', textX, y + 122);

    type(600, 32);
    ctx.fillStyle = palette.inkDim;
    ctx.fillText(content.rarest.points, textX, y + 170);

    y += RAREST_H + 26;
  }

  if (content.standings.length > 0) {
    type(800, 26);
    ctx.fillStyle = palette.inkFaint;
    ctx.fillText('STANDINGS', PAD, y + 26);
    y += 50;

    for (const row of content.standings) {
      // The player's own row is filled rather than outlined, so they find
      // themselves before they read anything.
      ctx.fillStyle = row.isYou ? palette.accent : palette.surface;
      roundedRect(ctx, PAD, y, INNER, ROW_H, 20);
      ctx.fill();
      if (!row.isYou) {
        ctx.strokeStyle = palette.line;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      const ink = row.isYou ? palette.accentInk : palette.ink;
      const baseline = y + 56;

      type(800, 34);
      ctx.fillStyle = row.isYou ? palette.accentInk : palette.inkFaint;
      ctx.textAlign = 'right';
      ctx.fillText(String(row.rank), PAD + 52, baseline);

      ctx.textAlign = 'right';
      type(800, 38);
      ctx.fillStyle = ink;
      const scoreCellWidth = ctx.measureText(row.score).width;
      ctx.fillText(row.score, CARD_W - PAD - 28, baseline);

      ctx.textAlign = 'left';
      const nameWidth = INNER - 76 - 28 - scoreCellWidth - 24 - (row.isYou ? 110 : 0);
      const name = fitType(ctx, row.name, nameWidth, 700, [38, 32, 28], palette.font, 1);
      const drawn = name.lines[0] ?? '';
      ctx.fillStyle = ink;
      ctx.fillText(drawn, PAD + 76, baseline);

      if (row.isYou) {
        const pillX = PAD + 76 + ctx.measureText(drawn).width + 16;
        ctx.fillStyle = palette.ink;
        roundedRect(ctx, pillX, baseline - 30, 88, 38, 19);
        ctx.fill();
        type(800, 22);
        ctx.fillStyle = palette.bg;
        ctx.fillText('YOU', pillX + 20, baseline - 4);
      }

      y += ROW_H + ROW_GAP;
    }
  }

  type(800, 38);
  ctx.fillStyle = palette.ink;
  ctx.fillText(content.appName, PAD, CARD_H - 116);
  type(600, 32);
  ctx.fillStyle = palette.inkFaint;
  ctx.fillText(content.url, PAD, CARD_H - 68);
}

/* ------------------------------------------------------------------ render */

export interface RenderDeps {
  /** jsdom has neither a 2D renderer nor `toBlob`, so tests supply their own surface. */
  createCanvas?: (width: number, height: number) => HTMLCanvasElement;
  /** Turns the pictogram's data URL into something `drawImage` accepts. */
  loadImage?: (src: string) => Promise<CanvasImageSource>;
}

function defaultCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function defaultLoadImage(src: string): Promise<CanvasImageSource> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The plate pictogram could not be decoded'));
    image.src = src;
  });
}

function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The card could not be encoded'))),
      'image/png',
    );
  });
}

export async function renderTripCard(
  trip: TripState,
  meId: string | null,
  deps: RenderDeps = {},
): Promise<Blob> {
  const canvas = (deps.createCanvas ?? defaultCanvas)(CARD_W, CARD_H);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser has no 2D canvas');

  const content = buildCardContent(trip, meId);

  // A trip with nothing on it still gets a card; it just has no plate to show.
  let pictogram: { image: CanvasImageSource; width: number; height: number } | null = null;
  if (content.rarest) {
    const svg = plateImage(content.rarest.plate, PICTOGRAM_W);
    const image = await (deps.loadImage ?? defaultLoadImage)(svg.src);
    pictogram = { image, width: svg.width, height: svg.height };
  }

  drawCard(ctx, content, readPalette(), pictogram);

  return await toPng(canvas);
}

/* ------------------------------------------------------------------- share */

export type ShareOutcome = 'shared' | 'copied' | 'downloaded';

const isAbort = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

/**
 * Gets the card off the phone by whatever route the browser actually allows,
 * best first. Every step is optional somewhere: the share sheet is missing on
 * desktop Firefox, image writes to the clipboard are missing on iOS Safari, and
 * a download is the only thing everything can do.
 */
export async function shareCard(
  blob: Blob,
  filename: string,
  text: string,
): Promise<ShareOutcome> {
  try {
    const files = [new File([blob], filename, { type: 'image/png' })];
    // `canShare` is the only reliable way to ask whether files are allowed;
    // plenty of browsers expose `share` and then reject anything with a file.
    if (typeof navigator.canShare === 'function' && navigator.canShare({ files })) {
      await navigator.share({ files, text });
      return 'shared';
    }
  } catch (error) {
    // A dismissed sheet is a decision, not a failure. Falling through would put
    // the card somewhere the player has just declined to send it.
    if (isAbort(error)) return 'shared';
  }

  try {
    if (navigator.clipboard?.write && typeof ClipboardItem === 'function') {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      return 'copied';
    }
  } catch {
    // Permissions, or a clipboard that will not take images. Try the file.
  }

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    return 'downloaded';
  } finally {
    // Revoked on the next turn rather than immediately: Safari starts the save
    // asynchronously, and a URL pulled out from under it saves nothing.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/* ------------------------------------------------------------------ button */

const RESET_MS = 2_500;

const OUTCOME_LABEL: Record<ShareOutcome, string> = {
  shared: 'Shared',
  copied: 'Card copied',
  downloaded: 'Card saved',
};

export function ShareTripButton({ trip, meId }: { trip: TripState; meId: string | null }) {
  const [status, setStatus] = useState<'idle' | 'working' | ShareOutcome>('idle');
  const [failed, setFailed] = useState(false);

  const run = async () => {
    setStatus('working');
    setFailed(false);

    try {
      const blob = await renderTripCard(trip, meId);
      const outcome = await shareCard(
        blob,
        cardFilename(trip),
        shareText(buildCardContent(trip, meId)),
      );
      setStatus(outcome);
      setTimeout(() => setStatus('idle'), RESET_MS);
    } catch {
      /*
       * Said out loud, unlike the swallowed clipboard failure in Trips'
       * copyCode. A trip code that will not copy is still printed on the screen
       * to read out, so silence there costs nothing; a card that never appears
       * leaves the player with nothing at all and no reason to believe the
       * button did anything.
       */
      setStatus('idle');
      setFailed(true);
    }
  };

  const label =
    status === 'working'
      ? 'Making card…'
      : status === 'idle'
        ? 'Share result'
        : OUTCOME_LABEL[status];

  return (
    <>
      <button
        type="button"
        className={status === 'working' ? 'btn working' : 'btn'}
        onClick={() => void run()}
        disabled={status === 'working'}
      >
        {label}
      </button>
      {failed && (
        <p className="field-error" role="status">
          Could not make a share card on this device. A screenshot will do the job.
        </p>
      )}
    </>
  );
}
